import { useCallback } from "react";
import type { CashierPaymentMethod, CurrencyPrimitive } from "@/api/cashier";
import { moneyToMajorUnits } from "@/utils/helpers";

export enum AmountValidation {
  BELOW = "below",
  ABOVE = "above",
}

export type AmountValidationError = {
  error: AmountValidation;
  message: string;
} | null;

export const useAmountValidator = () => {
  const amountValidator = useCallback(
    (
      amount: number | undefined,
      selectedProcessingCurrency: CurrencyPrimitive | undefined,
      selectedPaymentMethod: CashierPaymentMethod | undefined
    ): AmountValidationError => {
      if (
        amount === undefined ||
        !selectedPaymentMethod?.settings ||
        !selectedProcessingCurrency ||
        !selectedPaymentMethod.settings[selectedProcessingCurrency.code]
          ?.trading_currency?.limit
      ) {
        return null;
      }

      const paymentMethodSettings = selectedPaymentMethod.settings;

      const limit =
        paymentMethodSettings[selectedProcessingCurrency.code].trading_currency
          .limit;

      const min = limit.min;
      const max = limit.max;

      if (min) {
        const minValue = moneyToMajorUnits(min);
        if (
          minValue !== null &&
          Number.isFinite(minValue) &&
          minValue > amount
        ) {
          return {
            error: AmountValidation.BELOW,
            message: `The minimum amount to deposit is ${minValue} ${min.code}.`,
          };
        }
      }

      if (max) {
        const maxValue = moneyToMajorUnits(max);
        if (
          maxValue !== null &&
          Number.isFinite(maxValue) &&
          maxValue < amount
        ) {
          return {
            error: AmountValidation.ABOVE,
            message: `The maximum amount to deposit is ${maxValue} ${max.code}.`,
          };
        }
      }

      return null;
    },
    []
  );

  return amountValidator;
};
